import {
  BackComponent,
  CarrierLogo,
  FilePicker,
  Loader,
  TabPane,
  TabPanel,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { FeatureFlag } from '@/enums/featureFlags'
import { Redirect } from '@/routes/Redirect'
import { paths } from '@/routes/paths'
import { useClaim, useUploadClaimFiles } from '@/services'
import { useFeatureFlag } from '@/services/feature-flags'
import { useGoToPath } from '@/utils'
import { Button } from '@surein/ui'
import { Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FilesTab } from './FilesTab/Component'
import { MessagesTab } from './MessagesTab/Component'
import {
  CarrierRow,
  ClaimDetailRow,
  ClaimDetails,
  ClaimViewContainer,
  ClaimViewPageMainContent,
  TitleRow,
} from './styles'

const ClaimDetail = ({
  label,
  value,
}: {
  label: string
  value: string | null | undefined
}) => (
  <ClaimDetailRow>
    <Typography color={ThemeColor.b50} variant="p2Body">
      {label}
    </Typography>
    <Typography variant="p1Body">{value || '--'}</Typography>
  </ClaimDetailRow>
)

const TABS = ['messages', 'files'] as const
type ClaimTab = typeof TABS[number]

export const ClaimViewPage = () => {
  const { t } = useTranslation()
  const {
    data: claimsOverviewFeatureFlag,
    isLoading: isClaimsOverviewFeatureFlagLoading,
  } = useFeatureFlag(FeatureFlag.CLAIMS_OVERVIEW)
  const { companyId, claimId, tab } = useParams()
  const activeTab: ClaimTab = TABS.includes(tab as ClaimTab)
    ? (tab as ClaimTab)
    : 'messages'

  const { data: claim, isLoading, isError } = useClaim(companyId, claimId)

  const goToMessages = useGoToPath(
    paths.viewClaimWithKey({
      companyId: companyId ?? '',
      claimId: claimId ?? '',
      tab: 'messages',
    }),
  )
  const goToFiles = useGoToPath(
    paths.viewClaimWithKey({
      companyId: companyId ?? '',
      claimId: claimId ?? '',
      tab: 'files',
    }),
  )

  const handleTabChange = (tabKey: string) => {
    if (tabKey === 'files') {
      goToFiles()
    } else {
      goToMessages()
    }
  }

  const { uploadFiles, isUploading } = useUploadClaimFiles(companyId, claimId)

  const handleUploadFiles = (newFiles: File[]) => {
    uploadFiles(newFiles)
    goToFiles()
  }

  if (isLoading || isClaimsOverviewFeatureFlagLoading) {
    return <Loader />
  }

  if (!claimsOverviewFeatureFlag?.state) {
    return <Redirect to={paths.dashboard} />
  }

  if (isError || !claim) {
    return <Redirect to={paths.claims} />
  }

  return (
    <ClaimViewPageMainContent size="sm">
      <BackComponent />
      <ClaimViewContainer>
        <TitleRow>
          <Typography bold variant="h4">
            {claim.title || t('claimsPage.table.noTitle')}
          </Typography>
          {activeTab !== 'files' && (
            <FilePicker multiple perFile onPick={handleUploadFiles}>
              <Button icon={Upload} variant="outline" disabled={isUploading}>
                {t('claimsPage.detail.files.upload')}
              </Button>
            </FilePicker>
          )}
        </TitleRow>
        {claim.insurance && (
          <CarrierRow>
            <CarrierLogo name={claim.insurance.carrier.logo_url} width="40px" />
            <Typography color={ThemeColor.b50} variant="p1Body">
              {claim.insurance.carrier.name}
            </Typography>
          </CarrierRow>
        )}
        <ClaimDetails>
          <ClaimDetail
            label={t('claimsPage.detail.mainRiskInsured')}
            value={claim.insurance?.main_risk_insured}
          />
          <ClaimDetail
            label={t('claimsPage.detail.insurer')}
            value={claim.insurance?.carrier.name}
          />
          <ClaimDetail
            label={t('claimsPage.table.policyNumber')}
            value={claim.insurance?.policy_number}
          />
        </ClaimDetails>
        <TabPanel
          defaultActiveKey={activeTab}
          shouldChangeUrl={false}
          onChange={handleTabChange}
        >
          <TabPane key="messages" text={t('claimsPage.detail.tabs.messages')}>
            <MessagesTab companyId={companyId ?? ''} claimId={claimId ?? ''} />
          </TabPane>
          <TabPane key="files" text={t('claimsPage.detail.tabs.files')}>
            <FilesTab companyId={companyId ?? ''} claimId={claimId ?? ''} />
          </TabPane>
        </TabPanel>
      </ClaimViewContainer>
    </ClaimViewPageMainContent>
  )
}
